import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { NavItem } from '../../models/nav-item.model';

/**
 * Reusable dropdown panel that renders a flat list of nav links.
 * The parent is responsible for positioning and open/closed state.
 */
@Component({
  selector: 'xbh-nav-dropdown',
  templateUrl: './nav-dropdown.component.html',
  styleUrls: ['./nav-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavDropdownComponent {
  @Input() items: NavItem[] = [];
  /** Emitted when focus leaves the panel naturally (Tab / click outside) — no focus return needed */
  @Output() readonly closed = new EventEmitter<void>();
  /** Emitted on keyboard dismiss (Escape / ArrowUp on first item) — parent should return focus to trigger */
  @Output() readonly closedByKey = new EventEmitter<void>();

  constructor(private readonly elRef: ElementRef<HTMLElement>) {}

  /** Focus the first interactive item in the panel. Called by the parent after opening. */
  focusFirst(): void {
    this.getMenuItems()[0]?.focus();
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const menuItems = this.getMenuItems();
    const currentIndex = menuItems.indexOf(
      this.elRef.nativeElement.ownerDocument.activeElement as HTMLElement,
    );

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (currentIndex === -1) {
        menuItems[0]?.focus();
      } else {
        // Wrap around to first item when on the last
        const next = menuItems[currentIndex + 1] ?? menuItems[0];
        next?.focus();
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (currentIndex === -1) {
        menuItems[menuItems.length - 1]?.focus();
      } else if (currentIndex > 0) {
        menuItems[currentIndex - 1]?.focus();
      } else {
        // ArrowUp on first item — return focus to trigger
        this.closedByKey.emit();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closedByKey.emit();
    }
  }

  onFocusout(event: FocusEvent): void {
    const related = event.relatedTarget as HTMLElement | null;
    if (!related?.closest('.myce-nav-dropdown')) {
      this.closed.emit();
    }
  }

  trackByLabel(_index: number, item: NavItem): string {
    return item.labelEn;
  }

  private getMenuItems(): HTMLElement[] {
    return Array.from(
      this.elRef.nativeElement.querySelectorAll<HTMLElement>('.myce-nav-dropdown__link'),
    );
  }
}
